import React from 'react';
import RenovationTrustApp from './src/RenovationTrustApp';
import * as Font from 'expo-font';
import { Asset } from 'expo-asset';
import { FontAwesome, Entypo, Ionicons, FontAwesome5, MaterialCommunityIcons, AntDesign, MaterialIcons, Feather, Foundation } from '@expo/vector-icons';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { View, Platform, Image } from 'react-native';
import { Video } from 'expo-av';
import * as ScreenOrientation from 'expo-screen-orientation';
import * as Localization from 'expo-localization';
import i18n from 'i18n-js';
import UserState from './src/contexts/UserState';
import Colors from './constants/Colors';
import jwt_decode from "jwt-decode";
import Constants from 'expo-constants';
import { Provider as PaperProvider } from 'react-native-paper';
import { NotifierWrapper } from 'react-native-notifier';
import { Storage } from './components';
import * as Sentry from 'sentry-expo';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import * as SplashScreen from 'expo-splash-screen';
import { RootSiblingParent } from 'react-native-root-siblings';
import 'react-native-get-random-values';
import AsyncStorage from '@react-native-async-storage/async-storage';

// localization/languages
let enCA = require('./assets/languages/en-CA.json');
let enUS = require('./assets/languages/en-US.json');
let fr = require('./assets/languages/fr.json');
i18n.translations = {
  'en-CA': enCA,
  'en-US': enUS,
  fr: fr,
};
i18n.locale = Localization.locale;
// i18n.locale = 'fr-CA';
// i18n.locale = 'en-US';
i18n.fallbacks = true;
// console.log('Localization: ', Localization, Localization.locale.replace(/\-/g, '_').toLowerCase())

if(Platform.OS !== 'web'){
  window.addEventListener = x => x;
}

//Sentry
Sentry.init({
  dsn: 'https://a7c5c011e2174a7db9f00ea1480f450f@o577258.ingest.sentry.io/5731843',
  enableInExpoDevelopment: true,
  debug: __DEV__ ? true : false,
});
// Access any @sentry/react-native exports via:
// Sentry.Native.*
// Access any @sentry/browser exports via:
// Sentry.Browser.*

export default function App() {
  const [isLoadingComplete, setLoadingComplete] = React.useState(false);
  const [hideSplashVideo, setHideSplashVideo] = React.useState(Platform.OS === 'web'); /// video shows
  // const [hideSplashVideo, setHideSplashVideo] = React.useState(true); // no video
  const [user, setUser] = React.useState({
    isLoggedIn: false
  });

  React.useEffect(() => {
    async function check(){
      const firstLaunch = await checkIfFirstLaunch();
      if(firstLaunch){
        Storage.clearAllItems();
      }
    }
    check();
  }, []);

  React.useEffect(() => {
    // turned off for testing
    if(Platform.OS === 'web'){
      checkToken();
    }
    // checkToken();
    lockScreen();
  }, []);

  React.useEffect(() => {
    async function prepare() {
      try {
        // Keep the splash screen visible while we fetch resources
        await SplashScreen.preventAutoHideAsync();
        let images = [
          require('./assets/img/icon.png'),
          require('./assets/img/logo_border_text_white.png'),
          require('./assets/img/default_project.jpg'),
          require('./assets/img/default_project_completed.jpg'),
          require('./assets/img/invite_banner.png'),
          require('./assets/img/how_disputes_work.png'),
          require('./assets/img/how_we_protect.jpg'),
          // require('./assets/splash/splash.png'),
          // require('./assets/splash/splash.mp4')
        ];
        if (Platform.OS !== 'web') {
          images.push(require('./assets/splash/splash.png'),);
          images.push(require('./assets/splash/splash1.mp4'));
        }
        const imageAssets = cacheImages(images);

        const fontAssets = cacheFonts([
          FontAwesome.font,
          Entypo.font,
          Ionicons.font,
          FontAwesome5.font,
          MaterialCommunityIcons.font,
          AntDesign.font,
          MaterialIcons.font,
          Feather.font,
          Foundation.font,
        ]);
        await Promise.all([...imageAssets, ...fontAssets]);
      } catch (e) {
        console.warn(e);
      } finally {
        // Tell the application to render
        setLoadingComplete(true);
        if (Platform.OS !== 'web') {
          startTimer();
        }
      }
    }

    prepare();
  }, []);

  const checkIfFirstLaunch = async () => {
    try {
      const hasLaunched = await AsyncStorage.getItem('hasLaunched');
      if(hasLaunched === null){
        AsyncStorage.setItem('hasLaunched', 'true');
        return true;
      }
      return false;
    }catch(err){
      return false;
    }
  }
  
  const checkToken = async () => {
    // console.log('checkToken()');
    let rtJWT = await Storage.getItem('rtJWT');
    // console.log('rtJWT: ', rtJWT);
    if (rtJWT) {
      const decodedJWT = jwt_decode(rtJWT);
      // console.log('decodedJWT: ', decodedJWT);
      let user = await Storage.getItem('user');
      // console.log('user: ', JSON.parse(user), Date.now(), decodedJWT.exp * 1000);
      if (user && Date.now() < (decodedJWT.exp * 1000)) {
        // console.log('token valid, proceeding to login');
        user = JSON.parse(user);
        setUser(user);
      }else{
        // console.log('token invalid, removing rtJWT & user');
        Storage.removeItem('rtJWT');
        Storage.removeItem('user');
      }
    }
  }

  const lockScreen = async () => {
    if (Platform.OS !== 'web') {
      try{
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
      }catch(err){
        console.log(err);
        Sentry[Platform.OS === 'web' ? 'Browser' : 'Native'].captureException(err);
      }
    }
  }

  function cacheImages(images) {
    return images.map(image => {
      if (typeof image === 'string') {
        return Image.prefetch(image);
      } else {
        return Asset.fromModule(image).downloadAsync();
      }
    });
  }

  function cacheFonts(fonts) {
    return fonts.map(font => Font.loadAsync(font));
  }

  const startTimer = () => {
    setInterval(() => {
      setHideSplashVideo(true);
    }, 6000);
  }

  const onLayoutRootView = React.useCallback(async () => {
    if (isLoadingComplete) {
      // This tells the splash screen to hide immediately! If we call this after
      // `setAppIsReady`, then we may see a blank screen while the app is
      // loading its initial state and rendering its first pixels. So instead,
      // we hide the splash screen once we know the root view has already
      // performed layout.
      await SplashScreen.hideAsync();
    }
  }, [isLoadingComplete]);

  if (!isLoadingComplete) {
    return null;
  }

  const webStyles = () => {
    if(Platform.OS === 'web'){
      return {
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.blue,
      }
    }
    return {};
  }

  if(!hideSplashVideo){
    return (
      <View style={[{ flex: 1 }, webStyles()]} onLayout={onLayoutRootView}>
          <Video
            source={require('./assets/splash/splash1.mp4')}
            rate={1.0}
            volume={1.0}
            isMuted={true}
            resizeMode={Platform.OS === 'web' ? 'contain' : 'cover'}
            shouldPlay
            style={[{ width: '100%', height: '100%' }]}
          />
      </View>
    );
  }
  
  return (
    <View style={{ flex: 1 }} onLayout={Platform.OS === 'web' ? null : onLayoutRootView}>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <PaperProvider>
          <SafeAreaProvider>
            <StatusBar style='light' />
            <UserState.Provider value={[user, setUser]}>
              <NotifierWrapper>
                <RootSiblingParent>
                  <RenovationTrustApp />
                </RootSiblingParent>
              </NotifierWrapper>
            </UserState.Provider>
          </SafeAreaProvider>
        </PaperProvider>
      </GestureHandlerRootView>
    </View>
  )
}
